import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { CheckedCombo } from '../../components/CheckedCombo';
import { useSaveFlow } from '../../components/confirm/useSaveFlow';
import { useConfirm } from '../../components/confirm/useConfirm';
import './QualifierDetailPage.css';

/** Bespoke Qualifier detail — the Qualifier-v7 prototype layout: a two-column
 *  Definition card (Active toggle · Retire Date · Modified) + a Sample / QC /
 *  Surrogate AUTOMATION matrix (one row each, cascading Matrix → Test → Method →
 *  Parameter multi-selects, plus QC Type on the QC row). Wired to the real
 *  /api/v1/qualifiers endpoints; saves through the shared useSaveFlow. */

interface Opt { id: string; name: string }
type SecKey = 'sample' | 'qc' | 'sur';
type Level = 'test' | 'method' | 'parameter';

interface Section {
  key: SecKey; label: string; tc: string; hasQcType: boolean;
  matrixUrl: string;
  testUrl: string; testParam: string;
  methodUrl: string; methodParam: string;
  paramUrl: string; paramParam: string;
}

const SECTIONS: Section[] = [
  { key: 'sample', label: 'Sample', tc: 'S', hasQcType: false,
    matrixUrl: '/api/v1/qualifiers/matrices',
    testUrl: '/api/v1/qualifiers/sample-tests', testParam: 'sampleMatrices',
    methodUrl: '/api/v1/qualifiers/sample-methods', methodParam: 'sampleTest',
    paramUrl: '/api/v1/qualifiers/sample-parameters', paramParam: 'sampleMethod' },
  { key: 'qc', label: 'QC', tc: 'Q', hasQcType: true,
    matrixUrl: '/api/v1/qualifiers/matrices',
    testUrl: '/api/v1/qualifiers/qc-tests', testParam: 'qcMatrix',
    methodUrl: '/api/v1/qualifiers/qc-methods', methodParam: 'qcTest',
    paramUrl: '/api/v1/qualifiers/qc-parameters', paramParam: 'qcMethod' },
  { key: 'sur', label: 'Surrogate', tc: 'R', hasQcType: false,
    matrixUrl: '/api/v1/qualifiers/sur-matrices',
    testUrl: '/api/v1/qualifiers/sur-tests', testParam: 'surMatrices',
    methodUrl: '/api/v1/qualifiers/sur-methods', methodParam: 'surTest',
    paramUrl: '/api/v1/qualifiers/sur-parameters', paramParam: 'surMethod' },
];
const SEC = Object.fromEntries(SECTIONS.map((s) => [s.key, s])) as Record<SecKey, Section>;

const TODAY = new Date().toISOString().slice(0, 10);
const iso = (d?: string | null) => (d ? d.slice(0, 10) : '');
const fmtMod = (d?: string | null) => {
  if (!d) return '';
  const s = d.replace('T', ' ');
  return `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(0, 4)} ${s.slice(11, 16)}`;
};

interface SecState { on: boolean; formula: string; qcType: string[]; matrix: string[]; test: string[]; method: string[]; parameter: string[] }
const blankSec = (): SecState => ({ on: false, formula: '', qcType: [], matrix: [], test: [], method: [], parameter: [] });
type SecOpts = { matrix: Opt[]; test: Opt[]; method: Opt[]; parameter: Opt[] };
const blankOpts = (): SecOpts => ({ matrix: [], test: [], method: [], parameter: [] });

interface Props {
  id: string | null;
  siblingIds?: string[];
  onClose: () => void;
  onSaved: () => void;
  onNavigate?: (id: string) => void;
}

export function QualifierDetailPage({ id, siblingIds = [], onClose, onSaved, onNavigate }: Props) {
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const isNew = id === null;

  // ── Definition ──
  const [qualifierId, setQualifierId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [definition, setDefinition] = useState('');
  const [category, setCategory] = useState('');
  const [comment, setComment] = useState('');
  const [retire, setRetire] = useState('');       // yyyy-MM-dd or ''
  const [modified, setModified] = useState('');
  const [symErr, setSymErr] = useState('');

  // ── Options / automation ──
  const [catOpts, setCatOpts] = useState<Opt[]>([]);
  const [qcTypeOpts, setQcTypeOpts] = useState<Opt[]>([]);
  const [secs, setSecs] = useState<Record<SecKey, SecState>>({ sample: blankSec(), qc: blankSec(), sur: blankSec() });
  const [opts, setOpts] = useState<Record<SecKey, SecOpts>>({ sample: blankOpts(), qc: blankOpts(), sur: blankOpts() });
  const [loading, setLoading] = useState(!isNew);
  // True once the user edits anything; drives whether Cancel confirms a discard.
  // Programmatic loads never flip it (they go through setState / setSecs, not the
  // user-edit setters below or the form's onChange).
  const [dirty, setDirty] = useState(false);

  const active = !retire || retire > TODAY;

  // Definition renders as a single-line box that auto-grows with its content
  // (prototype's `grow` textarea) — same height as Symbol when empty.
  const defRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const t = defRef.current;
    if (t) { t.style.height = '26px'; t.style.height = `${Math.max(26, t.scrollHeight)}px`; }
  }, [definition]);

  const fetchOpts = async (url: string, param?: string, parentIds?: string[]): Promise<Opt[]> => {
    const q = param && parentIds && parentIds.length ? `?${param}=${encodeURIComponent(parentIds.join(','))}` : '';
    const { data } = await api.get<{ data?: Opt[] }>(`${url}${q}`);
    return data.data ?? [];
  };
  const setSec = (k: SecKey, patch: Partial<SecState>) => { setDirty(true); setSecs((p) => ({ ...p, [k]: { ...p[k], ...patch } })); };
  const setSecOpts = (k: SecKey, patch: Partial<SecOpts>) => setOpts((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  // Cascade: a parent change reloads the child list and drops now-invalid child picks.
  const onMatrix = async (k: SecKey, matrix: string[]) => {
    setSec(k, { matrix, test: [], method: [], parameter: [] });
    setSecOpts(k, { test: [], method: [], parameter: [] });
    if (matrix.length) setSecOpts(k, { test: await fetchOpts(SEC[k].testUrl, SEC[k].testParam, matrix) });
  };
  const onTest = async (k: SecKey, test: string[]) => {
    setSec(k, { test, method: [], parameter: [] });
    setSecOpts(k, { method: [], parameter: [] });
    if (test.length) setSecOpts(k, { method: await fetchOpts(SEC[k].methodUrl, SEC[k].methodParam, test) });
  };
  const onMethod = async (k: SecKey, method: string[]) => {
    setSec(k, { method, parameter: [] });
    setSecOpts(k, { parameter: [] });
    if (method.length) setSecOpts(k, { parameter: await fetchOpts(SEC[k].paramUrl, SEC[k].paramParam, method) });
  };
  const onLevel = (k: SecKey, lvl: Level, v: string[]) =>
    lvl === 'test' ? onTest(k, v) : lvl === 'method' ? onMethod(k, v) : (setSec(k, { parameter: v }), Promise.resolve());

  // Load the static lists once, then (edit) the record + its cascade children.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [cats, qct] = await Promise.all([
        fetchOpts('/api/v1/qualifiers/categories'),
        fetchOpts('/api/v1/qualifiers/qc-types'),
      ]);
      const matrices = await Promise.all(SECTIONS.map((s) => fetchOpts(s.matrixUrl)));
      if (cancelled) return;
      setCatOpts(cats); setQcTypeOpts(qct);
      setOpts({
        sample: { ...blankOpts(), matrix: matrices[0]! },
        qc: { ...blankOpts(), matrix: matrices[1]! },
        sur: { ...blankOpts(), matrix: matrices[2]! },
      });

      if (isNew) { setLoading(false); return; }
      const { data } = await api.get<{ data?: Record<string, unknown> }>(`/api/v1/qualifiers/${id}`);
      const d = data.data ?? {};
      if (cancelled) return;
      const arr = (v: unknown) => (Array.isArray(v) ? (v as unknown[]).map(String) : []);
      setQualifierId(String(d.qualifierId ?? ''));
      setSymbol(String(d.symbol ?? ''));
      setDefinition(String(d.definition ?? ''));
      setCategory(d.category ? String(d.category) : '');
      setComment(String(d.comment ?? ''));
      setRetire(iso(d.retireDate as string | null));
      setModified(fmtMod(d.lastUpdatedDate as string | null));
      const load = (formula: unknown, qcType: unknown, m: unknown, t: unknown, me: unknown, pa: unknown): SecState => ({
        on: !!(String(formula ?? '') || arr(m).length || arr(t).length || arr(me).length || arr(pa).length || arr(qcType).length),
        formula: String(formula ?? ''), qcType: arr(qcType), matrix: arr(m), test: arr(t), method: arr(me), parameter: arr(pa),
      });
      const next = {
        sample: load(d.sampleFormula, [], d.sampleMatrices, d.sampleTest, d.sampleMethod, d.sampleParameter),
        qc: load(d.qcFormula, d.qcType, d.qcMatrix, d.qcTest, d.qcMethod, d.qcParameter),
        sur: load(d.surFormula, [], d.surMatrices, d.surTest, d.surMethod, d.surParameter),
      };
      setSecs(next);
      // Load cascade children so the stored Oids render as names.
      for (const k of ['sample', 'qc', 'sur'] as SecKey[]) {
        const s = next[k];
        const patch: Partial<SecOpts> = {};
        if (s.matrix.length) patch.test = await fetchOpts(SEC[k].testUrl, SEC[k].testParam, s.matrix);
        if (s.test.length) patch.method = await fetchOpts(SEC[k].methodUrl, SEC[k].methodParam, s.test);
        if (s.method.length) patch.parameter = await fetchOpts(SEC[k].paramUrl, SEC[k].paramParam, s.method);
        if (!cancelled) setSecOpts(k, patch);
      }
      if (!cancelled) { setLoading(false); setDirty(false); }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  // Active toggle ↔ Retire Date (prototype rules).
  const toggleActive = (on: boolean) => {
    setDirty(true);
    if (!on) setRetire(TODAY);                      // deactivate → retire today
    else if (retire && retire <= TODAY) setRetire(''); // reactivate → clear a past date
  };
  const onRetireChange = (v: string) => { setDirty(true); setRetire(v); };
  const clearRetire = () => { setDirty(true); setRetire(''); };

  const idx = useMemo(() => (id ? siblingIds.indexOf(id) : -1), [id, siblingIds]);
  const go = (delta: number) => { const n = siblingIds[idx + delta]; if (n && onNavigate) onNavigate(n); };

  const validate = () => {
    const s = symbol.trim(), d = definition.trim();
    setSymErr('');
    if (!s) { setSymErr('Symbol is required.'); return false; }
    if (!d) return false;
    return true;
  };

  const buildPayload = () => {
    const sec = (k: SecKey) => { const s = secs[k]; const off = !s.on; return {
      formula: off ? null : (s.formula.trim() || null),
      qcType: off ? [] : s.qcType, matrix: off ? [] : s.matrix,
      test: off ? [] : s.test, method: off ? [] : s.method, parameter: off ? [] : s.parameter,
    }; };
    const sa = sec('sample'), qc = sec('qc'), su = sec('sur');
    return {
      symbol: symbol.trim(), definition: definition.trim(), category: category || null, comment: comment.trim() || null,
      sampleFormula: sa.formula, sampleMatrices: sa.matrix, sampleTest: sa.test, sampleMethod: sa.method, sampleParameter: sa.parameter,
      qcType: qc.qcType, qcFormula: qc.formula, qcMatrix: qc.matrix, qcTest: qc.test, qcMethod: qc.method, qcParameter: qc.parameter,
      surFormula: su.formula, surMatrices: su.matrix, surTest: su.test, surMethod: su.method, surParameter: su.parameter,
      retireDate: retire || null,
    };
  };

  const save = async (after: 'stay' | 'new' | 'close') => {
    if (!validate()) return;
    await runSave({
      confirmMessage: 'Do you want to Save?',
      formName: 'Qualifier', auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        const payload = buildPayload();
        if (isNew) await api.post('/api/v1/qualifiers', payload);
        else await api.put(`/api/v1/qualifiers/${id}`, payload);
      },
      successMessage: 'Saved successfully.',
      // useSaveFlow AWAITS the "Saved successfully" dialog, so this runs only
      // after the user clicks OK. Save / Save & Close → back to the list;
      // Save & New → a fresh blank form (ALPACA convention).
      onSaved: () => {
        setDirty(false);
        onSaved();                        // refresh the list behind us
        if (after === 'new') resetNew();
        else onClose();
      },
    });
  };

  const resetNew = () => {
    setQualifierId(''); setSymbol(''); setDefinition(''); setCategory(''); setComment(''); setRetire(''); setModified(''); setSymErr('');
    setSecs({ sample: blankSec(), qc: blankSec(), sur: blankSec() });
    setOpts((p) => ({ sample: { ...p.sample, test: [], method: [], parameter: [] }, qc: { ...p.qc, test: [], method: [], parameter: [] }, sur: { ...p.sur, test: [], method: [], parameter: [] } }));
    setDirty(false);
  };

  // Only ask "Discard changes?" when there is actually something unsaved; a clean
  // form (nothing edited, or just saved) closes straight to the list.
  const cancel = async () => {
    if (!dirty) { onClose(); return; }
    const ok = await confirm({ title: 'Discard changes?', tone: 'default', confirmLabel: 'Leave',
      message: 'Leave this qualifier without saving?' });
    if (ok) onClose();
  };

  const names = (o: Opt[], ids: string[]) => { const m = new Map(o.map((x) => [x.id, x.name])); return ids.map((i) => m.get(i)).filter((n): n is string => !!n); };
  const idsFrom = (o: Opt[], ns: string[]) => { const m = new Map(o.map((x) => [x.name, x.id])); return ns.map((n) => m.get(n)).filter((x): x is string => !!x); };

  const Actions = () => (
    <div className="qzd-actbar">
      <button className="qzd-ca" onClick={() => save('stay')}><i className="qzd-gi sv">✓</i> Save</button>
      <button className="qzd-ca qzd-ghost" onClick={() => save('new')}><i className="qzd-gi rf">＋</i> Save &amp; New</button>
      <button className="qzd-ca qzd-ghost" onClick={() => save('close')}><i className="qzd-gi ed">⤶</i> Save &amp; Close</button>
      <button className="qzd-ca qzd-ghost" onClick={cancel}><i className="qzd-gi dl">↩</i> Cancel</button>
    </div>
  );

  return (
    <div className="qzd" onChange={() => setDirty(true)}>
      <div className="qzd-titlerow">
        <button className="qzd-back" onClick={cancel} title="Back to list">←</button>
        <div className="qzd-ftic">Q</div>
        <h1>Qualifier{symbol ? ` – ${symbol}` : ''}</h1>
        <div className="qzd-tracts">
          <Actions />
          <div className="qzd-recnav">
            <button disabled={isNew || idx <= 0} onClick={() => go(-1)} title="Previous">‹</button>
            <button disabled={isNew || idx < 0 || idx >= siblingIds.length - 1} onClick={() => go(1)} title="Next">›</button>
          </div>
        </div>
      </div>
      <div className="qzd-crumbs">
        Home<span className="sep">›</span>Document Management<span className="sep">›</span>
        <a onClick={onClose}>Qualifier</a><span className="sep">›</span>
        <b>{isNew ? 'New' : (qualifierId || '…')}</b>
      </div>

      {/* ── Definition ── */}
      <div className="qzd-card acc-def">
        <div className="qzd-sechd"><span className="qzd-si">Q</span>Definition</div>
        <div className="qzd-fgrid">
          <label>Qualifier ID</label>
          <input className="qzd-fin" value={qualifierId} readOnly placeholder="(auto)" />
          <label className="req">Symbol</label>
          <div>
            <input className={`qzd-fin${symErr ? ' bad' : !symbol.trim() ? ' reqempty' : ''}`} maxLength={100} value={symbol}
              onChange={(e) => { setSymbol(e.target.value); setSymErr(''); }} />
            {symErr && <div className="qzd-err">{symErr}</div>}
          </div>

          <label className="req">Definition</label>
          <textarea ref={defRef} className={`qzd-fin qzd-full qzd-grow${!definition.trim() ? ' reqempty' : ''}`} rows={1}
            value={definition} onChange={(e) => setDefinition(e.target.value)} />

          <label>Category</label>
          <select className="qzd-fin" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">N/A</option>
            {catOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <label>Active</label>
          <div className="qzd-inl">
            <label className="qzd-sw">
              <input type="checkbox" checked={active} onChange={(e) => toggleActive(e.target.checked)} />
              <span className="qzd-tr" /><span className="qzd-sl">{active ? 'Active' : 'Inactive'}</span>
            </label>
            <span className="qzd-note">{!active ? 'Hidden from Result Entry / SDMS pickers'
              : retire ? `Retires on ${iso(retire)} — still selectable until then` : 'Available in Result Entry / SDMS pickers'}</span>
          </div>

          <label className={active ? '' : 'req'}>Retire Date</label>
          <div className="qzd-inl">
            <input className="qzd-fin qzd-dt" type="date" value={retire} onChange={(e) => onRetireChange(e.target.value)} />
            <a className="qzd-clr" onClick={clearRetire}>Clear</a>
          </div>
          <label>Modified</label>
          <input className="qzd-fin" value={modified} readOnly />

          <label>Comment</label>
          <textarea className="qzd-fin qzd-full" rows={1} value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
      </div>

      {/* ── Automation ── */}
      <div className="qzd-card acc-auto">
        <div className="qzd-sechd">
          <span className="qzd-si">⚡</span>Automation
          <span className="qzd-hint">— switch a row on to auto-apply this qualifier; blank cells mean “any”.</span>
          <span className="qzd-legend">
            <span><i style={{ background: 'var(--qzd-sam)' }} />Sample</span>
            <span><i style={{ background: 'var(--qzd-qc)' }} />QC</span>
            <span><i style={{ background: 'var(--qzd-sur)' }} />Surrogate</span>
          </span>
        </div>
        <div className="qzd-mxcard">
          <div className="qzd-scroll">
            <table className="qzd-amx">
              <thead><tr><th>Apply to</th><th>QC Type</th><th>Formula</th><th>Matrix</th><th>Test</th><th>Method</th><th>Parameter</th></tr></thead>
              <tbody>
                {SECTIONS.map((sc) => {
                  const s = secs[sc.key]; const o = opts[sc.key]; const on = s.on;
                  return (
                    <tr key={sc.key} className={`t-${sc.tc}${on ? ' on' : ' dis'}`}>
                      <td className="qzd-rl">
                        <label className="qzd-tpill">
                          <input type="checkbox" checked={on} onChange={(e) => setSec(sc.key, { on: e.target.checked })} /> {sc.label}
                        </label>
                      </td>
                      <td>{sc.hasQcType
                        ? <CheckedCombo options={qcTypeOpts.map((x) => x.name)} values={names(qcTypeOpts, s.qcType)}
                            onChange={(ns) => setSec('qc', { qcType: idsFrom(qcTypeOpts, ns) })} disabled={!on}
                            placeholder="Any" summaryJoin="; " pageSize={10} />
                        : <div className="qzd-na">—</div>}</td>
                      <td><input className="qzd-fin" value={s.formula} disabled={!on} placeholder="e.g. Result < RL"
                        onChange={(e) => setSec(sc.key, { formula: e.target.value })} /></td>
                      <td><CheckedCombo options={o.matrix.map((x) => x.name)} values={names(o.matrix, s.matrix)}
                        onChange={(ns) => onMatrix(sc.key, idsFrom(o.matrix, ns))} disabled={!on} placeholder="Any" summaryJoin="; " pageSize={10} /></td>
                      <td><CheckedCombo options={o.test.map((x) => x.name)} values={names(o.test, s.test)}
                        onChange={(ns) => onLevel(sc.key, 'test', idsFrom(o.test, ns))} disabled={!on || !s.matrix.length} placeholder="Any" summaryJoin="; " pageSize={10} /></td>
                      <td><CheckedCombo options={o.method.map((x) => x.name)} values={names(o.method, s.method)}
                        onChange={(ns) => onLevel(sc.key, 'method', idsFrom(o.method, ns))} disabled={!on || !s.test.length} placeholder="Any" summaryJoin="; " pageSize={10} /></td>
                      <td><CheckedCombo options={o.parameter.map((x) => x.name)} values={names(o.parameter, s.parameter)}
                        onChange={(ns) => onLevel(sc.key, 'parameter', idsFrom(o.parameter, ns))} disabled={!on || !s.method.length} placeholder="Any" summaryJoin="; " pageSize={10} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="qzd-actbar qzd-bottom"><Actions /></div>
      {loading && <div className="qzd-loading">Loading…</div>}
    </div>
  );
}
